/**
 * Loss Tracking Service - API integration for inventory loss management
 * 
 * Features:
 * - Record inventory losses (damage, expiry, theft, etc.)
 * - Approve/reject loss records
 * - Get loss analytics and summaries
 * - Shelf-life compatible batch loss recording
 */

import api from './api';

// Loss reasons matching backend enum
export const LossReason = {
  EXPIRED: 'expired',
  NEAR_EXPIRY: 'near_expiry',
  DAMAGED: 'damaged',
  SPOILED: 'spoiled',
  THEFT: 'theft',
  QUALITY_ISSUE: 'quality_issue',
  RECALL: 'recall',
  OTHER: 'other'
};

// Loss status matching backend enum
export const LossStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  WRITTEN_OFF: 'written_off'
};

// Human-readable labels
export const LossReasonLabels = {
  [LossReason.EXPIRED]: 'Expired',
  [LossReason.NEAR_EXPIRY]: 'Near Expiry',
  [LossReason.DAMAGED]: 'Damaged',
  [LossReason.SPOILED]: 'Spoiled',
  [LossReason.THEFT]: 'Theft',
  [LossReason.QUALITY_ISSUE]: 'Quality Issue',
  [LossReason.RECALL]: 'Recall',
  [LossReason.OTHER]: 'Other'
};

export const LossStatusLabels = {
  [LossStatus.PENDING]: 'Pending Review',
  [LossStatus.APPROVED]: 'Approved',
  [LossStatus.REJECTED]: 'Rejected',
  [LossStatus.WRITTEN_OFF]: 'Written Off'
};

const lossTrackingService = {
  /**
   * Get all loss records with optional filters
   * @param {Object} filters - Query filters
   * @returns {Promise<{data: Array, total: number}>}
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/loss-tracking', { params: filters });
    return response.data;
  },

  /**
   * Get a single loss record by ID
   * @param {number} id - Loss record ID
   * @returns {Promise<Object>}
   */
  getById: async (id) => {
    const response = await api.get(`/loss-tracking/${id}`);
    return response.data;
  },

  /**
   * Create a new loss record
   * @param {Object} data - Loss record data
   * @returns {Promise<Object>}
   */
  create: async (data) => {
    const response = await api.post('/loss-tracking', data);
    return response.data;
  },

  /**
   * Update a pending loss record
   * @param {number} id - Loss record ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>}
   */
  update: async (id, data) => {
    const response = await api.put(`/loss-tracking/${id}`, data);
    return response.data;
  },

  /**
   * Approve, reject, or write off a loss record
   * @param {number} id - Loss record ID
   * @param {string} status - New status (approved, rejected, written_off)
   * @param {string} notes - Optional approval notes
   * @returns {Promise<Object>}
   */
  approve: async (id, status, notes = '') => {
    const response = await api.post(`/loss-tracking/${id}/approve`, {
      status,
      notes: notes || undefined
    });
    return response.data;
  },

  /**
   * Delete a pending loss record
   * @param {number} id - Loss record ID
   * @returns {Promise<void>}
   */
  delete: async (id) => {
    await api.delete(`/loss-tracking/${id}`);
  },

  /**
   * Get loss summary/analytics
   * @param {string} fromDate - Start date (YYYY-MM-DD)
   * @param {string} toDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>}
   */
  getSummary: async (fromDate = null, toDate = null) => {
    const params = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    const response = await api.get('/loss-tracking/summary', { params });
    return response.data;
  },

  /**
   * Get losses for a specific product
   * @param {number} productId - Product ID
   * @returns {Promise<Array>}
   */
  getByProduct: async (productId) => {
    const response = await api.get(`/loss-tracking/product/${productId}`);
    return response.data;
  },

  /**
   * Get losses for a specific batch (shelf-life integration)
   * @param {number} batchId - Batch ID
   * @returns {Promise<Array>}
   */
  getByBatch: async (batchId) => {
    const response = await api.get(`/loss-tracking/batch/${batchId}`);
    return response.data;
  },

  /**
   * Get pending losses approaching expiry
   * @param {number} daysThreshold - Days threshold (default: 7)
   * @returns {Promise<Array>}
   */
  getExpiring: async (daysThreshold = 7) => {
    const response = await api.get('/loss-tracking/expiring', {
      params: { daysThreshold }
    });
    return response.data;
  },

  /**
   * Record batch expiry loss (for shelf-life module)
   * @param {Object} data - Batch loss data
   * @returns {Promise<Object>}
   */
  recordBatchExpiry: async (data) => {
    const response = await api.post('/loss-tracking/batch-expiry', data);
    return response.data;
  },

  /**
   * Bulk record batch expiry losses
   * @param {Array} losses - Array of batch loss data
   * @returns {Promise<Array>}
   */
  bulkRecordBatchExpiry: async (losses) => {
    const response = await api.post('/loss-tracking/batch-expiry/bulk', losses);
    return response.data;
  }
};

export default lossTrackingService;